import { useEffect, useRef, useState, type DragEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { AlertCircle, FileText, Trash2, Upload } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Button } from "../../components/Button/Button";
import { Modal } from "../../components/Modal/Modal";
import { formatByteCount, formatBytes, formatMiB, shortSha } from "../../lib/bytes";
import { ApiError, NetworkError, describeError } from "../../lib/errors";
import { sha256Hex } from "../../lib/sha256";
import {
  deletePlayerCloudSave,
  getPlayerCloudSave,
  uploadPlayerCloudSave,
} from "./api";
import { formatRelative } from "./formatTime";
import type { Player, PlayerCloudSave } from "./types";
import styles from "./PlayerCloudSaveDialogs.module.css";

// === Upload / Replace ================================================= //

interface UploadProps {
  projectId: string;
  player: Player;
  /** The slot as the card showed it; decides the title and the If-Match. */
  current: PlayerCloudSave;
  maxBytes: number;
  onClose: () => void;
  /**
   * The slot moved, or was written: the card has to be refetched. Awaited
   * before the dialog reports the write done.
   */
  onChanged: () => void | Promise<void>;
  /** The write landed and the card is fresh; `message` is the toast. */
  onDone: (message: string) => void;
}

/** What the 409 told us, resolved into a sentence the dialog can show. */
interface Conflict {
  body: string;
}

/**
 * One dialog for both Upload… and Replace…; only the words change with
 * whether the slot holds something. The file's size and sha256 are computed
 * in the browser before anything is sent, so the size is refused here and
 * the diff row can say what is about to be written.
 */
export function CloudSaveUploadDialog({
  projectId,
  player,
  current: live,
  maxBytes,
  onClose,
  onChanged,
  onDone,
}: UploadProps) {
  // The slot as it was when the dialog OPENED, not as the card's query
  // holds it now: the precondition has to name what the operator looked
  // at. A refetch while the dialog is open would otherwise move it to the
  // new sha256 — or from «empty» to a save — and the write would pass a
  // check that was meant to stop it.
  const [current] = useState(live);
  const replacing = current.exists;
  const fileRef = useRef<HTMLInputElement>(null);
  const primaryRef = useRef<HTMLButtonElement>(null);
  // The PUT in flight, so Cancel can end it, and whether the dialog is
  // still there for the answers that arrive after it closed.
  const inflight = useRef<AbortController | null>(null);
  const open = useRef(true);
  useEffect(
    () => () => {
      open.current = false;
      inflight.current?.abort();
    },
    [],
  );
  // Bumped on every pick, so a hash that finishes for a file the operator has
  // since swapped out does not land on the new one.
  const pickSeq = useRef(0);
  const [file, setFile] = useState<File | null>(null);
  // The bytes and their hash, read once: what was hashed is what is sent.
  const [bytes, setBytes] = useState<ArrayBuffer | null>(null);
  const [sha, setSha] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [conflict, setConflict] = useState<Conflict | null>(null);
  const [readError, setReadError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: ArrayBuffer) => {
      const ac = new AbortController();
      inflight.current = ac;
      return uploadPlayerCloudSave(
        projectId,
        player.id,
        payload,
        current.exists ? current.sha256 : null,
        ac.signal,
      );
    },
    onSuccess: async () => {
      // The card is fresh before the toast says «done».
      await onChanged();
      if (open.current) onDone(replacing ? "Cloud save replaced." : "Cloud save uploaded.");
    },
    onError: (err) => {
      if (!isConflict(err)) return;
      // The 409 itself says whether the slot was emptied or rewritten, and
      // that is shown at once — the Replace button is gone from the first
      // render after the answer. Who wrote and when is one read away and
      // replaces the sentence when it arrives.
      setConflict(conflictFromDetails(err.details));
      void describeConflict(projectId, player.id).then((c) => {
        if (c && open.current) setConflict(c);
      });
    },
  });

  // Cancel while the PUT is in flight ends it: nothing has been written
  // until the server answers, and an operator on a slow link must not be
  // locked in a dialog for a 64 MiB upload they have changed their mind on.
  function cancel() {
    inflight.current?.abort();
    onClose();
  }

  async function pick(next: File | undefined) {
    if (!next) return;
    const seq = ++pickSeq.current;
    setFile(next);
    setBytes(null);
    setSha(null);
    setReadError(null);
    if (next.size === 0 || next.size > maxBytes) return;
    try {
      const data = await readBytes(next);
      const hex = await sha256Hex(data);
      if (pickSeq.current === seq) {
        setBytes(data);
        setSha(hex);
      }
    } catch {
      // A file the browser cannot read — moved since it was picked, or on
      // a drive that went away — would otherwise sit at «hashing…» forever.
      if (pickSeq.current === seq) setReadError("Couldn't read the file. Pick it again.");
    }
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragging(false);
    void pick(e.dataTransfer.files?.[0]);
  }

  const tooLarge = !!file && file.size > maxBytes;
  const empty = !!file && file.size === 0;
  const hashing = !!file && !tooLarge && !empty && !readError && sha === null;
  const ready = !!file && !tooLarge && !empty && sha !== null && bytes !== null;
  const pending = mutation.isPending;

  // Picking a file unmounts the drop zone the keyboard was on, which would
  // drop focus to the body; the footer's button is where the next key
  // press belongs.
  useEffect(() => {
    if (ready) primaryRef.current?.focus();
  }, [ready]);
  // A 409 is the conflict view's, never the red line's.
  const failure =
    mutation.error && !isConflict(mutation.error) ? uploadFailure(mutation.error) : null;

  const title = replacing ? "Replace cloud save" : "Upload cloud save";

  // Out of the tab order: the drop zone (and, once a file is in, Change)
  // is the one focusable way to the picker, so a keyboard reaches one
  // control for one action.
  const picker = (
    <input
      ref={fileRef}
      type="file"
      className={styles.fileInput}
      aria-label="Save file"
      tabIndex={-1}
      onChange={(e) => {
        const next = e.target.files?.[0];
        // Reset so picking the same file twice fires change again.
        e.target.value = "";
        void pick(next);
      }}
    />
  );

  const fileBox = file && (
    <div className={tooLarge || empty ? `${styles.fileBox} ${styles.invalid}` : styles.fileBox}>
      <span className={styles.fIco}>
        <FileText size={14} aria-hidden="true" />
      </span>
      <span className={styles.fMain}>
        <span className={styles.fName}>{file.name}</span>
        <span className={styles.fMeta}>
          {formatByteCount(file.size)}
          {sha ? ` · sha256 ${shortSha(sha)}` : hashing ? " · hashing…" : ""}
        </span>
      </span>
      {!conflict && (
        <Button
          variant="secondary"
          size="sm"
          disabled={pending}
          onClick={() => fileRef.current?.click()}
        >
          Change
        </Button>
      )}
    </div>
  );

  if (conflict) {
    return (
      <Modal
        open
        onClose={() => {
          void onChanged();
          onClose();
        }}
        title={title}
        width="sm"
        icon={<Upload size={17} aria-hidden="true" />}
        iconTone="neutral"
        footer={
          <Button
            variant="secondary"
            onClick={() => {
              void onChanged();
              onClose();
            }}
          >
            Close
          </Button>
        }
      >
        <div className={styles.alertGap}>
          <Alert
            variant="warn"
            title="The save changed while this dialog was open"
          >
            {conflict.body}
          </Alert>
        </div>
        {fileBox}
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={cancel}
      title={title}
      width="sm"
      icon={<Upload size={17} aria-hidden="true" />}
      iconTone="neutral"
      footer={
        <>
          <Button variant="secondary" onClick={cancel}>
            Cancel
          </Button>
          <Button
            ref={primaryRef}
            variant="primary"
            disabled={!ready || pending}
            loading={pending}
            onClick={() => bytes && mutation.mutate(bytes)}
          >
            {pending ? "Uploading…" : replacing ? "Replace save" : "Upload save"}
          </Button>
        </>
      }
    >
      {picker}
      <p className={styles.lead}>
        {replacing ? (
          <>
            The file replaces <b>{player.display_name}</b>&apos;s current save
            as is — it is not opened or converted. The game gets a conflict on
            its next upload and re-downloads this one.
          </>
        ) : (
          <>
            Seeds <b>{player.display_name}</b>&apos;s slot with this file as
            is. The game finds it on its next status check and downloads it.
          </>
        )}
      </p>
      {(!file || tooLarge || empty) && (
        <div
          role="button"
          tabIndex={0}
          aria-label={`Choose a save file, up to ${formatMiB(maxBytes)}`}
          className={dragging ? `${styles.dropZone} ${styles.dragging}` : styles.dropZone}
          onClick={() => fileRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              fileRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(e) => {
            // dragleave fires for the zone's own children too; only a
            // leave that goes OUTSIDE the zone ends the highlight.
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDragging(false);
          }}
          onDrop={onDrop}
        >
          <b>Drop a file here</b> or <span className={styles.browse}>browse</span>{" "}
          · up to {formatMiB(maxBytes)}
        </div>
      )}
      {fileBox}
      {tooLarge && file && (
        <div className={styles.fieldError} role="alert">
          <AlertCircle size={12} aria-hidden="true" />
          {formatBytes(file.size)} is over the project&apos;s limit of{" "}
          {formatMiB(maxBytes)} (Config → Cloud saves → Max save size).
        </div>
      )}
      {empty && (
        <div className={styles.fieldError} role="alert">
          <AlertCircle size={12} aria-hidden="true" />
          The file is empty.
        </div>
      )}
      {readError && (
        <div className={styles.fieldError} role="alert">
          <AlertCircle size={12} aria-hidden="true" />
          {readError}
        </div>
      )}
      {replacing && current.exists && file && !tooLarge && !empty && (
        <div className={styles.diffRow}>
          <div>
            <div className={styles.sideKey}>Current</div>
            <div className={styles.sideVal}>
              {formatBytes(current.size_bytes)} · {formatRelative(current.updated_at)}
            </div>
            <div className={`${styles.sideVal} ${styles.sideMono}`}>
              {shortSha(current.sha256)}
            </div>
          </div>
          <div className={styles.arrow} aria-hidden="true">
            →
          </div>
          <div>
            <div className={styles.sideKey}>After</div>
            <div className={styles.sideVal}>{formatBytes(file.size)} · now</div>
            <div className={`${styles.sideVal} ${styles.sideMono}`}>
              {sha ? shortSha(sha) : "hashing…"}
            </div>
          </div>
        </div>
      )}
      {failure && (
        <div className={styles.trailingAlert}>
          <Alert variant="danger">{failure}</Alert>
        </div>
      )}
    </Modal>
  );
}

/** The file's bytes, through FileReader where Blob.arrayBuffer is missing. */
function readBytes(file: File): Promise<ArrayBuffer> {
  if (typeof file.arrayBuffer === "function") return file.arrayBuffer();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error("read failed"));
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.readAsArrayBuffer(file);
  });
}

/**
 * The 409 carries only the slot's new sha256. Who wrote it and when is one
 * read away, and that is the sentence the mockup shows — so the dialog reads
 * the slot again and says what it found. Null when the read fails: the
 * sentence built from the 409 itself stands then.
 */
async function describeConflict(projectId: string, playerId: string): Promise<Conflict | null> {
  try {
    const now = await getPlayerCloudSave(projectId, playerId);
    if (!now.exists) {
      return {
        body: "The save was deleted meanwhile. Close this dialog to see it, then decide again.",
      };
    }
    const who = now.source === "admin" ? "Another operator" : "The game";
    return {
      body: `${who} uploaded a new version ${formatRelative(now.updated_at)}. Close this dialog to see it, then decide again.`,
    };
  } catch {
    return null;
  }
}

function isConflict(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 409;
}

/** What the 409's own details say, before anything else is read. */
function conflictFromDetails(details: unknown): Conflict {
  const bag =
    details && typeof details === "object" ? (details as Record<string, unknown>) : {};
  if ("current_sha256" in bag && bag.current_sha256 === null) {
    return {
      body: "The save was deleted while this dialog was open. Close this dialog to see the card, then decide again.",
    };
  }
  return {
    body: "The save changed while this dialog was open. Close this dialog to see it, then decide again.",
  };
}

function uploadFailure(err: unknown): string {
  if (err instanceof ApiError) return describeError(err.code);
  if (err instanceof NetworkError) {
    return "Couldn't reach Social Hub API, so there is no telling whether the save went. Close this dialog and check the card.";
  }
  return "Something went wrong. Reload the page to check the card.";
}

// === Delete ============================================================ //

interface DeleteProps {
  projectId: string;
  player: Player;
  save: Extract<PlayerCloudSave, { exists: true }>;
  onClose: () => void;
  /** The slot is gone: the card has to be refetched, before onDone. */
  onChanged: () => void | Promise<void>;
  onDone: () => void;
}

/**
 * The danger dialog names the size and the date, so what is deleted is what
 * was seen. There is no version history, and the dialog says so.
 */
export function CloudSaveDeleteDialog({
  projectId,
  player,
  save,
  onClose,
  onChanged,
  onDone,
}: DeleteProps) {
  const mutation = useMutation({
    mutationFn: () => deletePlayerCloudSave(projectId, player.id),
    onSuccess: async () => {
      await onChanged();
      onDone();
    },
  });
  const pending = mutation.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      closeDisabled={pending}
      title="Delete cloud save"
      width="sm"
      icon={<Trash2 size={17} aria-hidden="true" />}
      iconTone="danger"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant="danger"
            loading={pending}
            disabled={pending}
            onClick={() => mutation.mutate()}
          >
            {pending ? "Deleting…" : "Delete save"}
          </Button>
        </>
      }
    >
      <p className={styles.lead}>
        Removes <b>{player.display_name}</b>&apos;s save — {formatBytes(save.size_bytes)},
        updated {formatRelative(save.updated_at)} — from the bucket. There is no
        previous version to fall back to: download it first if you may need it.
      </p>
      <Alert variant="danger">
        This cannot be undone. The game&apos;s local copy stays on the device;
        its next upload starts the slot over.
      </Alert>
      {mutation.error && (
        <div className={styles.trailingAlert}>
          <Alert variant="danger">{deleteFailure(mutation.error)}</Alert>
        </div>
      )}
    </Modal>
  );
}

function deleteFailure(err: unknown): string {
  if (err instanceof ApiError) return describeError(err.code);
  if (err instanceof NetworkError) {
    return "Couldn't reach Social Hub API, so there is no telling whether the save was deleted. Close this dialog and check the card.";
  }
  return "Something went wrong. Reload the page to check the card.";
}
